import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  TextField,
  Button,
  IconButton,
  Checkbox,
  FormControlLabel,
  Box,
  CircularProgress,
} from '@mui/material';

import Grid from '@mui/material/Grid';
import { Delete } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Close';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../components/AuthProvider';

interface Variation {
  variationId?: number;
  name: string;
  description: string;
  primaryImage: File | null;
  primaryImagePreview: string | null;
  additionalImages: File[];
  previewImages: string[];
  price: string;
  customizable?: boolean;
}

const API_BASE = process.env.REACT_APP_API_URL;

const AdminProductUpdateScreen: React.FC = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const { id } = useParams();
  const isEditMode = !!id;

  const [productName, setProductName] = useState('');
  const [productDescription, setProductDescription] = useState('');
  const [productDetails, setProductDetails] = useState('');
  const [productPrice, setProductPrice] = useState('');
  const [isLoading, setLoading] = useState(false);

  const [variations, setVariations] = useState<Variation[]>([
    {
      name: '',
      description: '',
      primaryImage: null,
      primaryImagePreview: null,
      additionalImages: [],
      previewImages: [],
      price: '',
      customizable: false,
    },
  ]);

  const handlePrimaryImageChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const updatedVariations = [...variations];
      updatedVariations[index].primaryImage = file;
      updatedVariations[index].primaryImagePreview = URL.createObjectURL(file);
      setVariations(updatedVariations);
    }
  };

  const handleAdditionalImagesChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    if (e.target.files) {
      const filesArray = Array.from(e.target.files);
      const updatedVariations = [...variations];
      updatedVariations[index].additionalImages = [
        ...updatedVariations[index].additionalImages,
        ...filesArray,
      ];
      const previewURLs = filesArray.map((file) => URL.createObjectURL(file));
      updatedVariations[index].previewImages = [
        ...updatedVariations[index].previewImages,
        ...previewURLs,
      ];
      setVariations(updatedVariations);
    }
  };

  const handleRemoveImage = (variationIndex: number, imageIndex: number) => {
    const updatedVariations = [...variations];
    updatedVariations[variationIndex].additionalImages = updatedVariations[variationIndex].additionalImages.filter(
      (_, i) => i !== imageIndex
    );
    updatedVariations[variationIndex].previewImages = updatedVariations[variationIndex].previewImages.filter(
      (_, i) => i !== imageIndex
    );
    setVariations(updatedVariations);
  };

  const handleRemovePrimaryImage = (index: number) => {
    const updatedVariations = [...variations];
    updatedVariations[index].primaryImage = null;
    updatedVariations[index].primaryImagePreview = null;
    setVariations(updatedVariations);
  };

  const handleVariationChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number
  ) => {
    const { name, value } = e.target;
    const updatedVariations = [...variations];
    updatedVariations[index] = { ...updatedVariations[index], [name]: value };
    setVariations(updatedVariations);
  };

  const handleCheckboxChange = (index: number) => {
    const updated = [...variations];
    updated[index].customizable = !updated[index].customizable;
    setVariations(updated);
  };

  const handleAddVariation = () => {
    setVariations([
      ...variations,
      {
        name: '',
        description: '',
        primaryImage: null,
        primaryImagePreview: null,
        additionalImages: [],
        previewImages: [],
        price: '',
        customizable: false,
      },
    ]);
  };

  const handleRemoveVariation = (index: number) => {
    setVariations((prev) => prev.filter((_, i) => i !== index));
  };

  useEffect(() => {
    if (!isAdmin) {
      navigate('/login');
      return;
    }

    if (isEditMode) {
      fetch(`${API_BASE}/api/products/${id}`)
        .then((res) => res.json())
        .then((data) => {
          setProductName(data.productName || '');
          setProductDescription(data.description || '');
          setProductDetails(data.details || '');
          setProductPrice(data.priceUSD || '');

          const preppedVariations = Object.keys(data.variationMap).map((key) => {
            const v = data.variationMap[key];
            v.images = v.images.filter((i: any) => i !== v.primaryImage);
            return {
              variationId: parseInt(key, 10),
              name: v.variationName,
              description: v.variationDescription,
              price: v.priceUSD || '',
              primaryImage: v.primaryImage || null,
              primaryImagePreview: v.primaryImage || null,
              additionalImages: v.images || [],
              previewImages: v.images || [],
              customizable: v.customizable,
            };
          });
          setVariations(preppedVariations);
        })
        .catch((err) => {
          console.error('Error fetching product for edit:', err);
        });
    }
  }, [isAdmin, isEditMode, id, navigate]);

  const handleSubmit = async () => {
    setLoading(true);
    const formData = new FormData();
    formData.append('productName', productName);
    formData.append('productDescription', productDescription);
    formData.append('productDetails', productDetails);
    formData.append('productPrice', productPrice);

    variations.forEach((variation, index) => {
      if (variation.variationId) {
        formData.append(`variations[${index}][variationId]`, variation.variationId.toString());
      }
      formData.append(`variations[${index}][name]`, variation.name);
      formData.append(`variations[${index}][description]`, variation.description);
      formData.append(`variations[${index}][price]`, variation.price);
      formData.append(`variations[${index}][customizable]`, String(variation.customizable));

      if (variation.primaryImage) {
        formData.append(`variations[${index}][primaryImage]`, variation.primaryImage);
      }
      variation.additionalImages.forEach((file, imgIndex) => {
        formData.append(`variations[${index}][additionalImages][${imgIndex}]`, file);
      });
    });

    try {
      let url = `${API_BASE}/api/products`;
      let method = 'POST';
      if (isEditMode) {
        url = `${API_BASE}/api/products/${id}`;
        method = 'PUT';
      }

      const response = await fetch(url, { method, body: formData });
      setLoading(false);
      if (response.ok) {
        const result = await response.json();
        if (result.productId) {
          navigate(`/product/${result.productId}?success=true`);
        } else if (isEditMode) {
          navigate(`/product/${id}?success=true`);
        }
      } else {
        console.error('Error saving product:', response.statusText);
      }
    } catch (error) {
      console.error('Error during submission:', error);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    if (!id) return;
    try {
      const response = await fetch(`${API_BASE}/api/products/${id}`, {
        method: 'DELETE',
      });
      setLoading(false);
      if (response.ok) {
        navigate('/?deleted=true');
      } else {
        console.error('Error deleting product:', response.statusText);
      }
    } catch (error) {
      console.error('Error deleting product:', error);
    }
  };

  if (!isAdmin) return null;

  return (
    <Container maxWidth="md" sx={{mt:5, mb:5}}>
      <Typography variant="h4" sx={{mb:3}}>
        {isEditMode ? 'Edit Product' : 'Add New Product'}
      </Typography>

      <Grid container spacing={2} style={{ marginBottom: 16 }}>
        <Grid item xs={12}>
          <TextField
            label="Product Name"
            value={productName}
            onChange={(e) => setProductName(e.target.value)}
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Product Description"
            value={productDescription}
            onChange={(e) => setProductDescription(e.target.value)}
            fullWidth
            multiline
            rows={2}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Product Details"
            value={productDetails}
            onChange={(e) => setProductDetails(e.target.value)}
            fullWidth
            multiline
            rows={2}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Base Price (USD)"
            value={productPrice}
            onChange={(e) => setProductPrice(e.target.value)}
            fullWidth
          />
        </Grid>
      </Grid>

      {variations.map((variation, index) => (
        <Grid container spacing={2} key={index} style={{ marginBottom: '20px' }}>
          <Grid item xs={12}>
            <Typography variant="h6">Variation {index + 1}</Typography>
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Variation Name"
              name="name"
              value={variation.name}
              fullWidth
              onChange={(e) => handleVariationChange(e, index)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Variation Description"
              name="description"
              value={variation.description}
              fullWidth
              onChange={(e) => handleVariationChange(e, index)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Variation Price"
              name="price"
              value={variation.price}
              fullWidth
              onChange={(e) => handleVariationChange(e, index)}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={variation.customizable}
                  onChange={() => handleCheckboxChange(index)}
                  color="primary"
                />
              }
              label="Customizable"
            />
          </Grid>

          <Grid item xs={12}>
            <Button variant="contained" component="label" color="primary">
              Upload Primary Image
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => handlePrimaryImageChange(e, index)}
              />
            </Button>
          </Grid>

          {variation.primaryImagePreview && (
            <Grid item xs={12}>
              <Typography variant="subtitle1">Primary Image Preview:</Typography>
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <img
                  src={variation.primaryImagePreview}
                  alt="Primary Image Preview"
                  style={{ width: '150px', height: '150px', objectFit: 'cover' }}
                />
                <IconButton
                  onClick={() => handleRemovePrimaryImage(index)}
                  size="small"
                  style={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </div>
            </Grid>
          )}

          <Grid item xs={12}>
            <Button variant="contained" component="label" color="primary">
              Upload Additional Images
              <input
                type="file"
                accept="image/*"
                multiple
                hidden
                onChange={(e) => handleAdditionalImagesChange(e, index)}
              />
            </Button>
          </Grid>

          {variation.previewImages.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Selected Image Previews for Variation {index + 1}:
              </Typography>
              <Grid container spacing={2}>
                {variation.previewImages.map((image, imgIndex) => (
                  <Grid item key={imgIndex} style={{ position: 'relative' }}>
                    <img
                      src={image}
                      alt={`Selected Preview ${imgIndex}`}
                      style={{
                        width: '100px',
                        height: '100px',
                        objectFit: 'cover',
                      }}
                    />
                    <IconButton
                      onClick={() => handleRemoveImage(index, imgIndex)}
                      size="small"
                      style={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        backgroundColor: 'rgba(255, 255, 255, 0.7)',
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          )}

          <Grid item xs={12}>
            <Button
              variant="contained"
              color="error"
              startIcon={<Delete />}
              onClick={() => handleRemoveVariation(index)}
            >
              Remove Variation
            </Button>
          </Grid>
        </Grid>
      ))}

      <Button variant="contained" color="secondary" onClick={handleAddVariation}>
        Add New Variation
      </Button>

      <Grid item xs={12}>
        <Button
          disabled={isLoading}
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          style={{ marginTop: '20px' }}
        >
          {isEditMode ? 'Save Product' : 'Submit Product'}
        </Button>
        {isEditMode && (
          <Button
            disabled={isLoading}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDelete}
            style={{ marginLeft: '10px', marginTop: '20px' }}
          >
            Delete Product
          </Button>
        )}
        {isLoading && (
          <Box display="flex" justifyContent="center" mb={2}>
            <CircularProgress />
          </Box>
        )}
      </Grid>
    </Container>
  );
};

export default AdminProductUpdateScreen;
